import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

const UPSERT = `mutation Push($articles: [KnowledgeArticleInput!]!) {
  upsertKnowledgeArticles(source: "docs", syncId: "2026-10-11", articles: $articles) {
    results { externalId status unchanged note error }
  }
}

{
  "articles": [
    {
      "externalId": "billing-invoices",
      "title": "How to download an invoice",
      "url": "https://help.acme.com/billing/invoices",
      "markdown": "Open **Billing**, choose an invoice and select **Download PDF**.",
      "roles": ["founder", "admin"],
      "plans": ["growth", "scale"]
    },
    {
      "externalId": "getting-started",
      "title": "Getting started",
      "html": "<p>Create your workspace, then invite your team.</p>",
      "roles": []
    }
  ]
}`;

const UPSERT_RESPONSE = `{
  "data": {
    "upsertKnowledgeArticles": {
      "results": [
        { "externalId": "billing-invoices", "status": "INDEXED", "unchanged": false,
          "note": null, "error": null },
        { "externalId": "getting-started", "status": "INDEXED", "unchanged": false,
          "note": null, "error": null }
      ]
    }
  }
}`;

const DELETE = `mutation Remove($ids: [String!]!) {
  deleteKnowledgeArticles(source: "docs", externalIds: $ids) {
    removed
  }
}

{ "ids": ["billing-invoices"] }`;

const FINISH = `mutation Finish {
  finishKnowledgeSync(source: "docs", syncId: "2026-10-11") {
    removed
  }
}`;

const NODE_SYNC = `// A nightly job on your server: push every article, then finish the refresh.
const SYNC_ID = new Date().toISOString();
const articles = await loadArticlesFromYourCms();

for (let i = 0; i < articles.length; i += 50) {
  const batch = articles.slice(i, i + 50).map((a) => ({
    externalId: a.id,
    title: a.title,
    markdown: a.markdown,
    url: a.publicUrl,
    roles: a.audienceRoles, // [] means everyone
  }));
  await revvly(
    \`mutation($articles: [KnowledgeArticleInput!]!, $syncId: String) {
      upsertKnowledgeArticles(source: "docs", syncId: $syncId, articles: $articles) {
        results { externalId status error }
      }
    }\`,
    { articles: batch, syncId: SYNC_ID },
  );
}

// Anything in the source that was not sent under SYNC_ID is removed.
await revvly(
  \`mutation($syncId: String!) {
    finishKnowledgeSync(source: "docs", syncId: $syncId) { removed }
  }\`,
  { syncId: SYNC_ID },
);`;

export function SupportKnowledgeApiGuide() {
  useDocsPage('Push your help articles to Revvly Support — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Push help articles</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Push your help articles to <em>Revvly Support</em>.
        </h1>
        <p className="dp-oneliner">
          If your help content lives in your own system, send it to Revvly instead of having a site
          crawled. The support AI answers from what you send, and you say who may read each article,
          so nothing is guessed.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="when">When to use this</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Push through the API</div>
            <div className="dp-field-v">
              Your articles are in a CMS, database or repository. The text is exactly what you send,
              and the audience is the one you state for each article.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Import a help centre</div>
            <div className="dp-field-v">
              You already publish on Intercom or Zendesk. Connect it in Support, Knowledge and
              Revvly reads it with your own token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Crawl a site</div>
            <div className="dp-field-v">
              Your help pages are public web pages and you have no export. Audience is worked out
              from the page and held for your review.
            </div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            A source filled through the API is not crawled and has no schedule. Revvly only changes
            it when you call the API (or an admin edits an article&rsquo;s audience in the
            dashboard).
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="key">Create a key that can add articles</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'API keys', 'Create key', 'Can add knowledge articles']}
          note="Admins and owners only. Revvly Support must be on for your workspace. The key is shown once."
        />
        <p className="dp-section-sub">
          The capability is called <span className="api-inline">KNOWLEDGE_WRITE</span>. It is
          separate from write access: a key that can add articles cannot change clients or
          subscriptions, and a write key cannot add articles. Keep it on your server only.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="upsert">Add or update articles</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/graphql</span>
          <span className="api-returns">application/json</span>
        </div>
        <p className="dp-section-sub">
          Call <span className="api-inline">upsertKnowledgeArticles</span> with the key as the
          Bearer token. Send up to 50 articles per call. Sending an article again with the same{' '}
          <span className="api-inline">externalId</span> updates it, and an article whose text,
          address and audience have not changed costs nothing.
        </p>
        <span className="api-code-label">Mutation and variables</span>
        <pre className="api-code">{UPSERT}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{UPSERT_RESPONSE}</pre>

        <h3 className="api-sub-heading">Fields of each article</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalId</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              Your own id for the article (max 200 characters). It is how a later push finds the
              same article.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">title</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">Shown with the answer as the source.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">markdown</span> or{' '}
              <span className="api-inline">html</span>
              <span className="api-badge api-badge-req">one of</span>
            </div>
            <div className="dp-field-v">
              The article body. Send exactly one; a row with both, or neither, is refused.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">roles</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              The support roles that may read it. Name each role by its internal key (shown on the
              role in the Roles panel of the Knowledge page, for example{' '}
              <span className="api-inline">founder</span>) or by its name, case-insensitive. The key
              never changes, so prefer it if a role may be renamed. An empty list means everyone,
              visitors included. It is required so an audience is never left to a guess, and an
              unknown role refuses the row.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">plans</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Narrows the article to some plans. Empty or omitted means every plan.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">url</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Where the article lives, as a full https address. It is the link in an answer.
            </div>
          </div>
        </div>

        <h3 className="api-sub-heading">Sources and results</h3>
        <p className="dp-section-sub">
          The <span className="api-inline">source</span> name (lowercase letters, digits,{' '}
          <span className="api-inline">-</span> and <span className="api-inline">_</span>; default{' '}
          <span className="api-inline">default</span>) groups your articles. The first push under a
          name creates the source, and you can have several. Each row succeeds or fails on its own:
          read <span className="api-inline">results</span>, which comes back in the order you sent,
          and look at <span className="api-inline">error</span> on each. Only a problem with the
          call itself fails the whole call.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            If an admin has set an article&rsquo;s audience in the dashboard, that wins over the one
            you send, and the row&rsquo;s <span className="api-inline">note</span> says so.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="delete">Remove articles</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">deleteKnowledgeArticles</span> removes up to 200 articles by
          your <span className="api-inline">externalId</span>. Ids that are not there are ignored,
          so it is safe to repeat.
        </p>
        <pre className="api-code">{DELETE}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="sync">Keep a source in step with yours</AnchorHeading>
        <p className="dp-section-sub">
          To mirror your content, send every article of a refresh with the same{' '}
          <span className="api-inline">syncId</span> (up to 128 characters), then close it with{' '}
          <span className="api-inline">finishKnowledgeSync</span>. Anything in the source that was
          not sent under that id is removed. This is the only way articles disappear without being
          named.
        </p>
        <pre className="api-code">{FINISH}</pre>
        <span className="api-code-label">A nightly job</span>
        <pre className="api-code">{NODE_SYNC}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">A typo removes nothing</div>
            <div className="dp-field-v">
              Finishing is refused when no article was sent with that{' '}
              <span className="api-inline">syncId</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A refused row keeps its copy</div>
            <div className="dp-field-v">
              If an article was refused in this refresh, the copy Revvly already holds is kept, not
              deleted.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A running refresh is safe</div>
            <div className="dp-field-v">
              Finishing only removes what was last written before this refresh&rsquo;s first
              article, so a refresh still running elsewhere is untouched.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="dashboard">See it in the dashboard</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge']}
          note="A pushed source shows as API, with its articles, their audience and the last result."
        />
        <p className="dp-section-sub">
          Pushed articles are searchable as soon as a row reports{' '}
          <span className="api-inline">INDEXED</span>. See also{' '}
          <Link to="/docs/guides/support-tokens">signing users in to the chat</Link>: the role value
          and attributes you send there decide which roles a person has, and the roles and plans you
          state here decide what their answers may draw on.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-knowledge">
              Build the knowledge base the AI answers from ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/support-ai">How the AI first line works ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
